import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, X, ExternalLink } from 'lucide-react';

interface PortfolioHolding {
  ticker: string;
  name?: string;
  shares: number;
  purchase_price: number;
  currentPrice: number;
  currentValue: number;
  totalGainLoss: number;
  totalGainLossPercent: number;
  dayChange: number;
  dayChangePercent: number;
  purchase_date: string;
}

interface PortfolioTableProps {
  holdings: PortfolioHolding[];
  onRemove: (ticker: string) => void;
  className?: string;
}

const th: React.CSSProperties = {
  padding: '0.625rem 1rem',
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  color: '#334155',
  background: '#0c111d',
  borderBottom: '1px solid #1a2235',
  whiteSpace: 'nowrap',
};
const thR: React.CSSProperties = { ...th, textAlign: 'right' };

export const PortfolioTable: React.FC<PortfolioTableProps> = ({
  holdings,
  onRemove,
  className = '',
}) => {
  const navigate = useNavigate();

  const fmt$ = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
  const fmt$0 = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
  const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

  /* ── Empty state ── */
  if (holdings.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 0.875rem',
        }}>
          <svg width="18" height="18" fill="none" stroke="#a78bfa" strokeWidth="1.75" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569', marginBottom: '0.25rem' }}>
          No holdings yet
        </p>
        <p style={{ fontSize: '0.78rem', color: '#1e3a5f' }}>
          Use "Add Holding" above to track your first position
        </p>
      </div>
    );
  }

  const DesktopRow: React.FC<{ h: PortfolioHolding }> = ({ h }) => {
    const isGain  = h.totalGainLoss >= 0;
    const glColor  = isGain ? '#10b981' : '#ef4444';
    const glBg     = isGain ? 'rgba(16,185,129,0.1)'  : 'rgba(239,68,68,0.1)';
    const glBorder = isGain ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)';
    const GlIcon   = isGain ? TrendingUp : TrendingDown;
    const invested = h.shares * h.purchase_price;

    const td: React.CSSProperties = {
      padding: '0.8125rem 1rem',
      borderBottom: '1px solid #0f1929',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    };

    return (
      <tr
        style={{ cursor: 'pointer', transition: 'background 0.15s' }}
        onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#141e30'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
        onClick={() => navigate(`/stock/${h.ticker}`)}
      >
        {/* Symbol + name */}
        <td style={td}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.6rem', fontWeight: 800, color: '#a78bfa',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {h.ticker.slice(0, 4)}
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em' }}>
                {h.ticker}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#334155', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {h.name || '—'}
              </div>
            </div>
          </div>
        </td>

        {/* Shares */}
        <td style={{ ...td, textAlign: 'right' }}>
          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
            {h.shares.toLocaleString()}
          </span>
        </td>

        {/* Avg buy */}
        <td style={{ ...td, textAlign: 'right' }}>
          <span style={{ fontSize: '0.8125rem', color: '#64748b', fontFamily: "'JetBrains Mono', monospace" }}>
            {fmt$(h.purchase_price)}
          </span>
        </td>

        {/* Current price */}
        <td style={{ ...td, textAlign: 'right' }}>
          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace" }}>
            {fmt$(h.currentPrice)}
          </span>
        </td>

        {/* Invested */}
        <td style={{ ...td, textAlign: 'right' }}>
          <span style={{ fontSize: '0.8125rem', color: '#64748b', fontFamily: "'JetBrains Mono', monospace" }}>
            {fmt$0(invested)}
          </span>
        </td>

        {/* Current value */}
        <td style={{ ...td, textAlign: 'right' }}>
          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace" }}>
            {fmt$0(h.currentValue)}
          </span>
        </td>

        {/* Total G/L */}
        <td style={{ ...td, textAlign: 'right' }}>
          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '0.2rem 0.5rem', borderRadius: 99,
              background: glBg, border: `1px solid ${glBorder}`,
              fontSize: '0.75rem', fontWeight: 700, color: glColor,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              <GlIcon size={11} strokeWidth={2.5} />
              {fmtPct(h.totalGainLossPercent)}
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: glColor, fontFamily: "'JetBrains Mono', monospace" }}>
              {h.totalGainLoss >= 0 ? '+' : ''}{fmt$0(h.totalGainLoss)}
            </span>
          </div>
        </td>

        {/* Actions */}
        <td style={{ ...td, textAlign: 'center', width: 72 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
            <button
              onClick={e => { e.stopPropagation(); navigate(`/stock/${h.ticker}`); }}
              aria-label={`View ${h.ticker}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: 'none', cursor: 'pointer', color: '#334155', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; e.currentTarget.style.color = '#60a5fa'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; }}
            >
              <ExternalLink size={13} strokeWidth={2} />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onRemove(h.ticker); }}
              aria-label={`Remove ${h.ticker}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: 'none', cursor: 'pointer', color: '#334155', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; }}
            >
              <X size={13} strokeWidth={2.5} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const MobileRow: React.FC<{ h: PortfolioHolding }> = ({ h }) => {
    const isGain = h.totalGainLoss >= 0;
    const glColor  = isGain ? '#10b981' : '#ef4444';
    const glBg     = isGain ? 'rgba(16,185,129,0.1)'  : 'rgba(239,68,68,0.1)';
    const glBorder = isGain ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)';

    const td: React.CSSProperties = {
      padding: '0.625rem 0.75rem',
      borderBottom: '1px solid #0f1929',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    };

    return (
      <tr
        style={{ cursor: 'pointer', transition: 'background 0.15s' }}
        onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#141e30'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
        onClick={() => navigate(`/stock/${h.ticker}`)}
      >
        <td style={td}>
          <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace" }}>
            {h.ticker}
          </div>
          <div style={{ fontSize: '0.68rem', color: '#334155' }}>{h.shares} shares</div>
        </td>
        <td style={{ ...td, textAlign: 'right' }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace" }}>
            {fmt$(h.currentPrice)}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#475569' }}>{fmt$0(h.currentValue)}</div>
        </td>
        <td style={{ ...td, textAlign: 'right' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '0.15rem 0.45rem', borderRadius: 99,
            background: glBg, border: `1px solid ${glBorder}`,
            fontSize: '0.72rem', fontWeight: 700, color: glColor,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {fmtPct(h.totalGainLossPercent)}
          </span>
        </td>
        <td style={{ ...td, width: 40, textAlign: 'center' }}>
          <button
            onClick={e => { e.stopPropagation(); onRemove(h.ticker); }}
            aria-label={`Remove ${h.ticker}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#334155', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; }}
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className={className} style={{ overflowX: 'auto' }}>
      {/* Desktop */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }} className="hidden lg:table">
        <thead>
          <tr>
            <th style={th}>Symbol</th>
            <th style={thR}>Shares</th>
            <th style={thR}>Avg Buy</th>
            <th style={thR}>Price</th>
            <th style={thR}>Invested</th>
            <th style={thR}>Value</th>
            <th style={thR}>Total G/L</th>
            <th style={{ ...th, width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {holdings.map(h => <DesktopRow key={h.ticker} h={h} />)}
        </tbody>
      </table>

      {/* Mobile / tablet */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }} className="lg:hidden">
        <thead>
          <tr>
            <th style={th}>Symbol</th>
            <th style={thR}>Price / Value</th>
            <th style={thR}>G/L</th>
            <th style={{ ...th, width: 48 }} />
          </tr>
        </thead>
        <tbody>
          {holdings.map(h => <MobileRow key={h.ticker} h={h} />)}
        </tbody>
      </table>
    </div>
  );
};
